import { useTheme } from "@mui/material";
import { useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MapProjectItem } from "../../../../api/psc";
import {
    ClusterMaxZoom,
    ClusterRadius,
    GetClusterLayer,
    GetClusterPointsLayer,
    GetUnClusterPointsLayer,
    MapCenter,
    MapDefaultZoom,
    MapIcons,
    SourceId,
    UnClusteredPointId,
} from "../../../../constants/MapConstants";
import {
    DefaultDarkMapStyles,
    DefaultMapStyles,
} from "../../../../constants/ProjectConstants";
import { DialogContext } from "../../../../contexts/DialogueContext";
import { RootState } from "../../../../slices/Store";
import ProjectDetailDialog from "../../../project-detail/ProjectDetailDialog";
import MapFilter from "../../components/MapFilter";
import { FetchPoints } from "../../slice/MapSlice";
import ProjectMapView from "./ProjectMapView";

// updated
export default function ProjectMapContainer() {
    const dispatch = useDispatch();
    const theme = useTheme();
    const mapContainer = useRef(null);
    const map = useRef(null);
    const mapState = useSelector((r: RootState) => r.map);
    const { openDialog } = useContext(DialogContext);

    const fetch = () => {
        FetchPoints(mapState, dispatch);
    };

    useEffect(() => {
        fetch();
    }, [mapState.id]);

    useEffect(() => {
        return () => {
            map.current?.remove();
        };
    }, []);

    useEffect(() => {
        const style =
            theme.palette.mode === "light"
                ? DefaultMapStyles
                : DefaultDarkMapStyles;

        if (!map.current) {
            import("mapbox-gl").then((mapboxgl) => {
                try {
                    mapboxgl.setRTLTextPlugin(
                        process.env.REACT_APP_MAP_STYLE_URL,
                        null
                    );
                } catch {}

                map.current = new mapboxgl.Map({
                    container: mapContainer.current,
                    style: style,
                    center: MapCenter,
                    zoom: MapDefaultZoom,
                });
            });
        }

        if (map.current?.loaded()) {
            setupMap();
        } else {
            if (map.current) {
                map.current.on("load", () => {
                    setupMap();
                });
            }
        }
    }, [mapState.points]);

    const openFilterDialog = () => {
        openDialog(<MapFilter />);
    };

    const getDataForPoint = (point: MapProjectItem) => {
        return {
            type: "Feature",
            properties: {
                status: point.isVisited ? 0 : point.subscriptionType,
                hashId: point.hashId,
            },
            geometry: {
                type: "Point",
                coordinates: [point.longitude, point.latitude],
            },
        };
    };

    const setupMap = () => {
        const source = map.current.getSource(SourceId);

        if (!mapState.points) {
            return;
        }

        const data = mapState.points.map((l) => getDataForPoint(l));

        // if source and layers aren't setup
        if (!source) {
            map.current.addSource(SourceId, {
                type: "geojson",
                cluster: true,
                clusterMaxZoom: ClusterMaxZoom, // Max zoom to cluster points on
                clusterRadius: ClusterRadius,
                data: {
                    type: "FeatureCollection",
                    features: data,
                },
            });

            map.current.addLayer(GetClusterLayer);
            map.current.addLayer(GetClusterPointsLayer);
            map.current.addLayer(GetUnClusterPointsLayer);

            for (const icon of MapIcons) {
                map.current.loadImage(icon.src, (err, img) => {
                    map.current.addImage(icon.id, img);
                });
            }

            map.current.on("click", UnClusteredPointId, (e) => {
                const coordinates = e.features[0];
                const hashId = coordinates.properties.hashId;
                openDialog(<ProjectDetailDialog projectHashId={hashId} />);
            });
        } else {
            source.setData({
                type: "FeatureCollection",
                features: data,
            });
        }
    };

    return (
        <ProjectMapView
            mapContainer={mapContainer}
            mapState={mapState}
            openFilterDialog={openFilterDialog}
        />
    );
}
